import { useQuery } from "@apollo/client";
import { TrashIcon } from "@heroicons/react/outline";
import { isEqual, sortBy } from "lodash";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CareerTrackRoleType,
  GetUserRolesQueryQuery,
  GetUserRolesQueryQueryVariables,
  SaveOrgWideCareerTrackMutationVariables,
} from "types/graphql-schema";

import Avatar from "@components/avatar/avatar";
import getUserRolesQuery from "@components/avatar/graphql/get-user-roles-query.ts/get-user-roles-query";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import { useLink } from "@components/link/link-helpers";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import TeamMemberCombobox from "../../team-member-combobox";
import useCareerPermissions from "../career-permissions";
import { getDefaultRoleTitle, personCareerPath } from "../helpers";
import { CareerTrackFormRole } from "./career-track-form";

const RoleDetailModal = ({
  organizationId,
  associatedTeam,
  careerTrackData,
  level,
  onClose,
  role,
  onRoleChange,
  onDelete,
  deleteDisabledReason,
  deleteConfirmation,
}: {
  organizationId: number;
  associatedTeam: UserComboboxOption | null;
  careerTrackData: Omit<SaveOrgWideCareerTrackMutationVariables, "roles">;
  onClose: () => void;
  level: number;
  role: CareerTrackFormRole;
  onRoleChange: (role: CareerTrackFormRole) => void;
  // Offered only for a role that exists; a role being created has nothing to
  // delete yet.
  onDelete?: () => void;
  // Set when the role can't be deleted from here, saying why.
  deleteDisabledReason?: string | null;
  deleteConfirmation?: { title: string; message: string };
}) => {
  const link = useLink();
  // Who holds a role is for admins to change; a department lead sees them.
  const { canManageLadder: canAssignPeople } = useCareerPermissions();
  const [startingRole] = useState<CareerTrackFormRole>(() => ({
    ...role,
    title: role.title?.trim()
      ? role.title
      : getDefaultRoleTitle(careerTrackData.title, level),
  }));
  const [proposedRole, setProposedRole] =
    useState<CareerTrackFormRole>(startingRole);
  const isDirty =
    proposedRole.title !== startingRole.title ||
    proposedRole.roleType !== startingRole.roleType ||
    !isEqual(
      sortBy(proposedRole.assignToUsers.map(({ id }) => id)),
      sortBy(startingRole.assignToUsers.map(({ id }) => id)),
    );
  // The role each member holds today, so the list says who is being moved
  // rather than just naming them.
  const memberIds = proposedRole.assignToUsers.map(({ id }) => id);
  const { data: userRolesData } = useQuery<
    GetUserRolesQueryQuery,
    GetUserRolesQueryQueryVariables
  >(getUserRolesQuery, {
    skip: memberIds.length === 0,
    variables: { userIds: memberIds },
  });
  const rolesByUserId = useMemo(
    () =>
      Object.fromEntries(
        (userRolesData?.users
          ? assertEdgesNonNull(userRolesData.users)
          : []
        ).map((user) => [
          user.id,
          (user.currentRoles ?? []).map((role) => role.title).join(", "),
        ]),
      ),
    [userRolesData],
  );

  const saveTooltip = useMemo(() => {
    if (!proposedRole.title?.trim()) return "Please enter a role name";
    return null;
  }, [proposedRole]);
  const canSave = !saveTooltip;
  const { ConfirmationDialog, confirm } = useConfirm(
    deleteConfirmation?.title ?? "Delete role?",
    deleteConfirmation?.message ?? "",
  );

  // Escape, a click outside and leaving for a member's page all ask first
  // when there are unsaved changes. Discard is already the answer.
  const { confirm: confirmDiscard, ConfirmationDialog: ConfirmDiscardDialog } =
    useConfirm("Discard changes?", "The changes you made will be lost.");
  const handleDismiss = async () => {
    if (isDirty && !(await confirmDiscard())) {
      return false;
    }
    onClose();
    return true;
  };

  // The confirm dialog is awaited, and a save can land meanwhile: delete
  // with the handler from then, not now.
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;
  const handleDelete = async () => {
    if (!onDelete || !(await confirm())) {
      return;
    }
    onDeleteRef.current?.();
    onClose();
  };

  return createPortal(
    <Modal
      open
      onClose={handleDismiss}
      alignedTop
      aria-label="Role detail modal"
    >
      <div className="p-6 flex flex-col">
        <ConfirmationDialog />
        <ConfirmDiscardDialog />
        <ModalTitle>Role Details</ModalTitle>

        <div className="flex flex-col gap-2 text-sm mt-6">
          <div className="flex items-center">
            <div className="w-1/3 text-gray-500">Department</div>
            <div className="w-2/3">
              {associatedTeam &&
                associatedTeam.type === UserComboboxOptionType.TEAM &&
                associatedTeam.title}
            </div>
          </div>
          <div className="flex items-center">
            <div className="w-1/3 text-gray-500">Track</div>
            <div className="w-2/3">{careerTrackData.title}</div>
          </div>
          <div className="flex items-center">
            <div className="w-1/3 text-gray-500">Level</div>
            <div className="w-2/3">{level}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-gray-500 text-xs uppercase font-semibold">
            Role name
          </div>
          <Input
            className="mt-2"
            value={proposedRole.title ?? ""}
            onChange={(evt) => {
              setProposedRole({
                ...proposedRole,
                title: evt.target.value,
              });
            }}
            aria-label="Role detail modal: Role name"
          />
          <div className="text-xs mt-1">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  proposedRole.roleType === CareerTrackRoleType.Management
                }
                onChange={(evt) => {
                  setProposedRole({
                    ...proposedRole,
                    roleType: evt.target.checked
                      ? CareerTrackRoleType.Management
                      : CareerTrackRoleType.Ic,
                  });
                }}
              />
              Management role
            </label>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-gray-500 text-xs uppercase font-semibold">
            Role members
          </div>
          {/* A department lead sees only the holders in their reporting line,
              so an empty list isn't an unheld role. */}
          {proposedRole.assignToUsers.length === 0 && (
            <div className="mt-2 text-sm text-gray-500">
              {!canAssignPeople && role.hasCurrentHolders
                ? "People outside your reporting line hold this role."
                : "No members assigned to this role."}
            </div>
          )}
          {!canAssignPeople && (
            <div className="mt-1 text-xs text-gray-500">
              Only an admin assigns people to this role.
            </div>
          )}
          <div className="mt-2 flex flex-col text-sm text-gray-600 divide-y border-t border-b empty:hidden">
            {proposedRole.assignToUsers.map((user) => (
              <div
                key={user.id}
                className="flex justify-between items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar user={user} size="4" />
                  {/* Their own view: their role's column, plus anything
                      written for them alone. */}
                  <button
                    type="button"
                    className="truncate hover:underline"
                    aria-label={`Role detail modal: View ${user.name}`}
                    onClick={async () => {
                      if (await handleDismiss()) {
                        link.redirect(
                          personCareerPath(organizationId, user.id),
                        );
                      }
                    }}
                  >
                    {user.name}
                  </button>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {rolesByUserId[user.id] && (
                    <span className="text-xs text-gray-400 tracking-tight">
                      {rolesByUserId[user.id]}
                    </span>
                  )}
                  {canAssignPeople && (
                    <button
                      type="button"
                      aria-label="Delete team member button"
                      onClick={() => {
                        setProposedRole({
                          ...proposedRole,
                          assignToUsers: proposedRole.assignToUsers.filter(
                            ({ id }) => id !== user.id,
                          ),
                        });
                      }}
                      className="rounded-sm hover:bg-gray-200 text-gray-500 hover:text-gray-800 px-1 py-0.5"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Under the list, so its dropdown doesn't cover the people who
              have just been added. */}
        {canAssignPeople && (
          <div className="mt-2">
            <TeamMemberCombobox
              closeOnSelect={false}
              excludeUserIds={proposedRole.assignToUsers.map(({ id }) => id)}
              onSelect={(userOpt) => {
                if (!userOpt || userOpt.type !== UserComboboxOptionType.USER) {
                  return;
                }
                setProposedRole((prev) => ({
                  ...prev,
                  assignToUsers: [...prev.assignToUsers, userOpt],
                }));
              }}
            />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-4">
          {/* Deleting takes the role's criteria and assignments with it, so
              it sits apart from the buttons that keep them. */}
          {onDelete ? (
            <Button
              theme={buttonTheme.redDanger}
              text="Delete"
              onClick={handleDelete}
              disabled={!!deleteDisabledReason}
              tooltip={deleteDisabledReason}
            />
          ) : (
            <div />
          )}
          <div className="flex items-center justify-end gap-2">
            <Button theme={buttonTheme.text} text="Discard" onClick={onClose} />
            <Button
              disabled={!canSave}
              theme="primary"
              tooltip={saveTooltip}
              text="Save"
              onClick={() => {
                // Only what this modal edits, on the role as it is now: an
                // autosave may have given its items ids since it opened.
                onRoleChange({
                  ...role,
                  title: proposedRole.title,
                  roleType: proposedRole.roleType,
                  assignToUsers: proposedRole.assignToUsers,
                });
                onClose();
              }}
            />
          </div>
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

export default RoleDetailModal;
